import { useCallback, useEffect, useRef, useState } from 'react';

import { appendPage } from '../unitPrices/runs.js';

/**
 * One keyset-paged list from the Console API (IP-112): the register's
 * holders, transactions and history, and Unit Prices' runs and prices. The
 * first page loads when the list's query changes (a new `fetchPage`, i.e. a
 * new filter) and on reload; Load more appends the next page by the server's
 * own nextCursor, each row shown once (appendPage on `rowKey`).
 *
 * As useOrders does, a counter drops a stale answer: a filter changed while a
 * page was in flight never lands that page on the new list. An error is the
 * server's words, said on the screen; the rows already shown stay.
 *
 * @param {(cursor: string|null) => Promise<{ items: object[], nextCursor: string|null }>|null} fetchPage
 *   one page; null while there is nothing to ask for (no fund chosen yet)
 * @param {string} rowKey the rows' own key
 * @returns {{ items: object[], nextCursor: string|null, loading: boolean, loadingMore: boolean, error: string|null,
 *   refreshedAt: Date|null, reload: () => void, loadMore: () => void, clearError: () => void }}
 */
export function usePagedList(fetchPage, rowKey) {
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const latest = useRef(0);

    const reload = useCallback(async () => {
        const ask = ++latest.current;
        if (!fetchPage) {
            setItems([]);
            setNextCursor(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const page = await fetchPage(null);
            if (ask !== latest.current) return;
            setItems(page.items ?? []);
            setNextCursor(page.nextCursor ?? null);
            setRefreshedAt(new Date());
        } catch (e) {
            if (ask === latest.current) setError(e.message);
        } finally {
            if (ask === latest.current) setLoading(false);
        }
    }, [fetchPage]);

    const loadMore = useCallback(async () => {
        if (!fetchPage || !nextCursor) return;
        const ask = latest.current;
        setLoadingMore(true);
        setError(null);
        try {
            const page = await fetchPage(nextCursor);
            if (ask !== latest.current) return;
            setItems((shown) => appendPage(shown, page.items ?? [], rowKey));
            setNextCursor(page.nextCursor ?? null);
        } catch (e) {
            if (ask === latest.current) setError(e.message);
        } finally {
            setLoadingMore(false);
        }
    }, [fetchPage, nextCursor, rowKey]);

    useEffect(() => {
        reload();
    }, [reload]);

    return { items, nextCursor, loading, loadingMore, error, refreshedAt, reload, loadMore, clearError: () => setError(null) };
}
